// Pure design tokens — no Mantine dependency
// These can be consumed by any app (including Next.js insights) without pulling in @mantine/core
//
// Source of truth: Figma "H2GC - Charlie" → Branding & Style Guide page.
// Named stops from the style guide: Accent #0273C7 (80/60/40/20 = #358FD2/#67ABDD/#9AC7E9/#CCE3F4),
// Primary #00324A (80/60/40/20 = #335B6E/#668492/#99ADB7/#CCD6DB); intermediate
// shades are interpolated to fill Mantine's 10-stop scales.

// Brand accent blue — light (0) to dark (9), brand Accent at index 7
export const accentColours = [
  '#e6f1f9',
  '#cce3f4',
  '#b3d5ee',
  '#9ac7e9',
  '#67abdd',
  '#358fd2',
  '#1f81cd',
  '#0273c7',
  '#025fa4',
  '#024c83',
] as const;

// Brand primary navy — light (0) to dark (9), brand Primary at index 7
export const navyColours = [
  '#e6ebed',
  '#ccd6db',
  '#b3c2c9',
  '#99adb7',
  '#668492',
  '#335b6e',
  '#1a4759',
  '#00324a',
  '#00283b',
  '#001a26',
] as const;

// Navy-tinted replacement for Mantine's `dark` scale (drives the dark colour
// scheme: body = dark[7], default surfaces = dark[6], borders = dark[4],
// text = dark[0])
export const darkSchemeColours = [
  '#ccd6db',
  '#b3c2c9',
  '#99adb7',
  '#668492',
  '#335b6e',
  '#1e4a5f',
  '#0b2b3c',
  '#06222f',
  '#041b26',
  '#001019',
] as const;

// "Blue" scheme — the all-navy app chrome from Figma's "ChatBot Screens Blue"
// section, where sidebar, canvas and panels are all brand Primary #00324A with
// Primary 80 #335B6E surfaces and white text.
//
// Mantine only models light/dark, so blue is an additive variant layered on the
// dark scheme: `[data-charlie-scheme='blue']` on the document root swaps the
// `dark` scale for this one, and every semantic dark variable
// (--mantine-color-body/-default/-default-border/-text/-dimmed) follows because
// Mantine defines them as `var(--mantine-color-dark-N)` references.
//
// Index roles (same as darkSchemeColours): 0 = text, 2 = dimmed,
// 3 = placeholder, 4 = border, 5 = surface hover, 6 = surface, 7 = body.
export const blueSchemeColours = [
  '#ffffff', // Secondary — body text on navy
  '#e6eef3',
  '#99adb7', // Primary 40 — dimmed text
  '#7f97a3',
  '#4d7488', // borders, visible against both body and surface
  '#3f6a7f', // surface hover
  '#335b6e', // Primary 80 — cards, sidebar active row, chat input
  '#00324a', // Primary — body/canvas/sidebar
  '#002639',
  '#001a26',
] as const;

// Destructive red — the delete/danger colour used by the Charlie sidebar's
// delete affordances. Not a named style-guide variable, so the scale is built
// the same way as `accentColours`: white tints below the brand stop (10/20/30/40
// /60/80/90 %) and black-ward shades (82.5 %, 66 %) above it, with the design
// colour #DF4040 at index 7 so `primaryShade: 7` renders it exactly.
export const redColours = [
  '#fcecec',
  '#f9d9d9',
  '#f6c6c6',
  '#f2b3b3',
  '#ec8c8c',
  '#e56666',
  '#e25353',
  '#df4040',
  '#b83535',
  '#932a2a',
] as const;

// Neutral grey — anchored on the style guide's neutrals rather than Mantine's
// blue-tinted defaults: Def Stroke #ECECEC (2), Surface #DADEDF (3) and
// Grey #4D4D4D (7). Intermediate stops are interpolated; the ramp keeps the
// shape of Mantine's `gray` so semantic roles land where they did before
// (light-scheme border = gray[4], dimmed text = gray[6]).
export const greyColours = [
  '#f8f8f8',
  '#f2f2f2',
  '#ececec',
  '#dadedf',
  '#c6cacb',
  '#a5a9aa',
  '#7d8081',
  '#4d4d4d',
  '#333333',
  '#1f1f1f',
] as const;

// Style-guide "Secondary": white — the body ground in light, the text colour on filled controls
export const secondaryColour = '#ffffff';

// Secondary ("dimmed") text in the light scheme: a navy-tinted grey between Primary 60 and 80.
// Mantine's default (grey[6], #7D8081) measures 3.98:1 on white; this clears AA on white (6.9:1),
// on the hover tint navy[0] (5.7:1) and on the selected Primary 20 fill navy[1] (4.7:1).
export const dimmedInk = '#495d67';

// Style-guide "Highlight" cyan — reserved for sparing emphasis (e.g. live/active
// indicators); not part of any Mantine scale yet
export const highlightColour = '#09fcff';
export const highlightColourSubtle = '#9dfeff';

// Markdown rules (horizontal rules, table borders) per scheme: the scheme's ink at 35% alpha.
// Light is Primary navy, dark the pale grey-blue ink, blue plain white against navy.
export const markdownRuleColours = {
  Light: 'rgba(0, 50, 74, 0.35)',
  Dark: 'rgba(204, 214, 219, 0.35)',
  Blue: 'rgba(255, 255, 255, 0.35)',
} as const;

// Mantine writes the `dark` palette's light/outline variants as literal rgba() rather than scale
// references, so the blue scheme restates them on its own navy stops.
export const blueSchemeDarkVariants = {
  Light: 'rgba(51, 91, 110, 0.15)',
  LightHover: 'rgba(51, 91, 110, 0.25)',
  OutlineHover: 'rgba(77, 116, 136, 0.15)',
} as const;

// Black and white at an alpha — scrims, shadows and hairlines that sit over any ground rather
// than being a palette colour. Dark-scheme shadows are deeper because 18% black is invisible on
// a dark ground.
export const overlayColours = {
  Scrim: 'rgba(0, 0, 0, 0.55)',
  Shadow: 'rgba(0, 0, 0, 0.18)',
  ShadowDark: 'rgba(0, 0, 0, 0.45)',
  Hairline: 'rgba(0, 0, 0, 0.08)',
  HairlineDark: 'rgba(255, 255, 255, 0.12)',
} as const;

// Forecast bars in the dark schemes: blue at 35% so a forecast reads as provisional beside the
// solid actuals.
export const forecastFillDark = 'rgba(103, 171, 221, 0.35)';

export const fontFamily =
  '"Instrument Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

// Code only (inline `code`, code blocks, tool traces). Figures are NOT set in it: Charlie draws
// KPI and table figures in the sans with tabular numerals (decision D-7) — see `tabularNums`.
export const fontFamilyMonospace =
  '"Roboto Mono", ui-monospace, Consolas, "Courier New", monospace';

// Newsreader serif: page, section and card titles (decision D-1, wired through the theme's
// `headings`) and the agent's markdown headings. Loaded by the apps via @fontsource.
export const serifFontFamily = 'Newsreader, Georgia, "Times New Roman", Times, serif';

// Heading scale from the Charlie frames (gap register G3, decision D-1): page title 36px,
// section title 28px, card title 24px, all regular weight. h4–h6 continue the steps for the
// smaller headings the admin pages use. Line height tightens as the size grows.
export const headingScale = {
  h1: { fontSize: 36, lineHeight: 1.2 },
  h2: { fontSize: 28, lineHeight: 1.25 },
  h3: { fontSize: 24, lineHeight: 1.3 },
  h4: { fontSize: 20, lineHeight: 1.35 },
  h5: { fontSize: 18, lineHeight: 1.4 },
  h6: { fontSize: 16, lineHeight: 1.45 },
} as const;

export const headingFontWeight = 400;

// Control geometry (gap register G4): Charlie's buttons and inputs are pills 48px tall, outlined
// with a 2px Primary border or filled with Primary 20 (`navyColours[1]`).
export const controlHeight = 48;
export const controlBorderWidth = 2;

// Card geometry (gap register G5): listing and chart cards are drawn at a 16px radius, account
// and plan cards at 24px.
export const cardRadius = 16;
export const featureCardRadius = 24;

// Figures (decision D-7): sans with tabular numerals, so columns of numbers line up without a
// monospace face. A plain style object — spread it into a vanilla-extract `style()` in an app.
// There is no class here on purpose: `internal/ui` ships no stylesheets, because insights
// fragments render it and ADR-16 bars CSS imports there.
export const tabularNums = { fontVariantNumeric: 'tabular-nums' } as const;
